import { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, StaticContext } from 'react-router'

import { MaOrderDetail } from './MaOrderDetail'
import {
  getTrackingInfo,
  isEnrolledProgram,
  isPhysicianAssociated
} from './MaOrderDetail.models'
import { resolveMaOrderBackTarget } from './maOrderBackTarget'
import {
  AnnounceMode,
  IClinAnnounceBarProps
} from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { OrderCancellationCodes } from '../../constants'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { OrderLineCancellationModal } from '../../features/OrderLineCancellationModal'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../services/Analytics'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetEnrolledPhysicians,
  cancelGetEnrolledPrograms,
  cancelGetProgramFromSearchIndexById,
  cancelGetReadOnlyOpaUrl,
  getEnrolledPhysicians,
  getEnrolledPrograms,
  getProgramFromSearchIndexById,
  getReadOnlyOpaUrl,
  setDocumentToHold,
  downloadOrderInvoice
} from '../../services/ApiService'
import { pushAccessFormWhenReady } from '../../services/pushAccessFormWhenReady'
import {
  OrderCancellationSaga,
  createOrderCancellationLogger,
  logOrderCancellationResult
} from '../../services/Saga'
import {
  getHoldDocumentForHold,
  useOrderLevelHolds
} from '../../services/Holds/holds'
import { isPatientDetailPostTrialAccess } from '../Patients/patientInitialsDisplay'
import { IError } from '../../types'
import { findOrderCancellationLookupCode } from '../../types/IOrder'
import { IRestEndpointState } from '../../types/IRestEndpointState'
import {
  DeleteOrderDto,
  HoldDto,
  InstituteDto,
  OrderDto,
  OrderLineDto,
  PhysiciansEnrolledSummaryDto,
  ProgramCatalogDto,
  UploadedDocumentDto
} from '../../types/swaggerTypes'
import { useErrorMessage } from '../../utils/useErrorMessage'
import {
  getUpdatedOrderState,
  getUpdatedOrderStateAfterDelete
} from '../OrderPages/OrderDetail/OrderDetail.model'
import { defaultEnrolledSearchParams } from '../Patients/CreatePatient/CreatePatient.model'
import useChangeBackgroundColor from '../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { ClinTheme } from '../../ClinTheme'
import {
  isOrderEligibleForInvoice,
  getPrimaryOrderLine
} from '../../utils/orderUtils'
import { OPAOrderTypes } from '../Patients/PatientDashboard/Patient.model'
import { buildOrderSuccessAnnounce } from './maOrderConfirmationAnnounce'
import { resolvePatientPhysicianId } from '../OpaCheckout/resolvePatientPhysicianId'

type LocationState = {
  endpoint?: string
  from: string
  orderDto?: OrderDto
  stockOrder?: boolean
  patientId?: number
  displayAnnounceOrderSuccess?: boolean
}

export interface IMaOrderDetailProps
  extends RouteComponentProps<{}, StaticContext, LocationState> {}

export const MaOrderDetailContainer: FunctionComponent<IMaOrderDetailProps> = ({
  history,
  location
}) => {
  const { t } = useTranslation()
  const { orderDto, stockOrder, displayAnnounceOrderSuccess } = location.state
  const isStockOrder = stockOrder || false
  const [isProgramLoading, setIsProgramLoading] = useState<boolean>(true)
  const [isPhysiciansLoading, setIsPhysiciansLoading] = useState<boolean>(true)
  const [isLoadingPatientAccessForm, setIsLoadingPatientAccessForm] =
    useState(true)
  const [order, setOrder] = useState<OrderDto | undefined>(orderDto)
  const [program, setProgram] = useState<ProgramCatalogDto>()
  const [physicians, setPhysicians] = useState<PhysiciansEnrolledSummaryDto[]>()
  const [patientAccessFormUrl, setPatientAccessFormUrl] = useState<
    string | undefined
  >()
  const [isCancelOrderlineModalOpen, setIsCancelOrderlineModalOpen] =
    useState<boolean>(false)
  const [deleteOrderDescription, setDeleteOrderDescription] = useState<
    DeleteOrderDto | undefined
  >()
  const [cancellationSubmitting, setCancellationSubmitting] =
    useState<boolean>(false)
  const isCancellingOrderRef = useRef<boolean>(false)
  const {
    institute,
    supportContact,
    user,
    dispatch,
    defaultShippingAddress,
    portfolioCountryCode
  } = useAppContext()
  const cancellationOptions =
    OrderCancellationCodes && Object.values(OrderCancellationCodes)
  const [cancellationHasError, setCancellationHasError] = useState<IError>()
  const [isLoadingCancelOrder, setIsLoadingCancelOrder] =
    useState<boolean>(false)

  // CLOS-14003 — suppress Compliance banner for PTA programmes even when the
  // Post Trial Access Hold is not yet present on the order holds payload.
  const isPostTrialAccessProgram = isPatientDetailPostTrialAccess(
    program?.program?.programStatus,
    program,
    defaultShippingAddress,
    portfolioCountryCode
  )
  const orderLevelHolds = useOrderLevelHolds(
    undefined,
    order,
    supportContact,
    isPostTrialAccessProgram
  )
  const [enrolledPrograms, setEnrolledPrograms] = useState<string[]>([])

  const [orderAnnounce, setOrderAnnounce] = React.useState<
    IClinAnnounceBarProps | undefined
  >()

  const [isDownloadingInvoice, setIsDownloadingInvoice] =
    useState<boolean>(false)

  const handleAccessFormUrlError = useErrorMessage(
    `There was an error fetching patient access form URL.`
  )
  const handleProgramError = useErrorMessage(
    `There was an error fetching program.`
  )
  const handleEnrolledPhysiciansError = useErrorMessage(
    `There was an error fetching the enrolled physicians.`
  )
  const primaryLine = getPrimaryOrderLine(order)
  const programId = primaryLine?.programId?.toString()
  const treatingPhysicianId = primaryLine?.physician?.physicianId
  const isPhysicianAssociatedToUser: boolean = isPhysicianAssociated(
    treatingPhysicianId,
    physicians
  )
  const isEnrolledProgramToUser: boolean = isEnrolledProgram(
    enrolledPrograms,
    programId
  )

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  useEffect(() => {
    if (displayAnnounceOrderSuccess) {
      setOrderAnnounce(buildOrderSuccessAnnounce())

      history.replace(location.pathname, {
        ...location.state,
        displayAnnounceOrderSuccess: false
      })
    }
  }, [])

  useEffect(() => {
    programId &&
      getProgramFromSearchIndexById(programId)
        .then((result) => {
          setProgram(result.data)
        })
        .catch((error) => handleProgramError(error))
        .finally(() => setIsProgramLoading(false))
    return () => {
      cancelGetProgramFromSearchIndexById()
    }
  }, [handleProgramError, programId])

  useEffect(() => {
    // Only make this call if is a physician associated to the user
    if (
      isPhysicianAssociatedToUser &&
      order?.opaAccessToken &&
      order?.orderNumber
    ) {
      getReadOnlyOpaUrl(Number(order?.orderNumber), {
        opaAccessToken: order?.opaAccessToken
      })
        .then((response) => {
          if (response.data.endPoint) {
            setPatientAccessFormUrl(response.data.endPoint)
          }
        })
        .catch((error) => handleAccessFormUrlError(error))
        .finally(() => setIsLoadingPatientAccessForm(false))
    } else if (order) {
      setIsLoadingPatientAccessForm(false)
    }
    return () => {
      cancelGetReadOnlyOpaUrl()
    }
  }, [handleAccessFormUrlError, order, isPhysicianAssociatedToUser])

  useEffect(() => {
    getEnrolledPrograms(defaultEnrolledSearchParams)
      .then((response) => {
        if (response) {
          setEnrolledPrograms(
            response.data.result.map((program) => program.programId.toString())
          )
        }
      })
      .catch((error: AxiosError) => {
        const { code, message } = error

        if (error.message === AuthError.RequestCancelled) {
          return
        }
        if (error.response) {
          const { title } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching enrolled access programs. ${
                title ? title : ''
              } ${message ? message : ''}`
            )
          )
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching enrolled access programs. ${message} ${
                code ? code : ''
              }`
            )
          )
        }

        return () => {
          cancelGetEnrolledPrograms()
        }
      })
  }, [programId, dispatch])

  useEffect(() => {
    programId &&
      getEnrolledPhysicians(programId)
        .then((response) => {
          response && setPhysicians(response.data.physicians)
        })
        .catch((error) => handleEnrolledPhysiciansError(error))
        .finally(() => {
          setIsPhysiciansLoading(false)
        })

    return () => {
      cancelGetEnrolledPhysicians()
    }
  }, [handleEnrolledPhysiciansError, programId])
  type Deps = [OrderDto, IRestEndpointState<InstituteDto>, string]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(AnalyticsPageEvent.ViewOrder, {
        'Order number': dependencies[0].orderNumber,
        Institution: dependencies[1].data?.instituteName
      })
    },
    [order, institute],
    (dependencies: Deps) => dependencies[0] && dependencies[1]
  )

  // CLOS-14579: patient actions (resupply, cancel) run under the patient's
  // current physician from the patient record. The order line physician is an
  // order fact and can differ; Oracle rejects patient calls under it.
  const resolvePatientPhysicianIdOrAnnounce = async (
    patientNumber: string | null | undefined
  ): Promise<string | undefined> => {
    const physicianId = await resolvePatientPhysicianId({ patientNumber })
    if (!physicianId) {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Error,
          'We could not find the treating physician for this order. Please open the patient from the Patients page to continue.',
          'Unable to start this order'
        )
      )
    }
    return physicianId
  }

  const handlePatientAccessForm = async () => {
    if (!programId || !patientAccessFormUrl) {
      history.push('/about/contact-us')
      return
    }
    // The read-only form is fetched by order + opaAccessToken; the physician
    // is only a route segment here, so the order's own physician is kept.
    const physicianId = treatingPhysicianId
      ? String(treatingPhysicianId)
      : await resolvePatientPhysicianIdOrAnnounce(primaryLine?.patientNumber)
    if (!physicianId) {
      return
    }
    history.push(
      `/programs/access-programs/${programId}/patient-access-form/${physicianId}`,
      {
        endpoint: patientAccessFormUrl,
        from: window.location.pathname
      }
    )
  }

  // CLOS-14590: Order resupply shows a spinner while the order-start check runs.
  const [isCheckingResupply, setIsCheckingResupply] = useState(false)

  const handleOrderResupply = async () => {
    const patientNumber = primaryLine?.patientNumber
    const physicianId = await resolvePatientPhysicianIdOrAnnounce(patientNumber)
    if (!physicianId) {
      return
    }
    order &&
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.PlaceOrderMAAgain, {
        programId,
        orderId: order.orderNumber
      })
    void pushAccessFormWhenReady(
      history,
      dispatch,
      {
        pathname: `/programs/access-programs/${programId}/patient-access-form/${physicianId}`,
        state: {
          orderType: OPAOrderTypes.Resupply,
          patientNumber: patientNumber,
          from: window.location.pathname
        }
      },
      { onCheckingChange: setIsCheckingResupply }
    )
  }

  const handleCancelOrder = () => {
    const updateOrderLineDto: DeleteOrderDto = {
      cancelReason: '',
      cancelComments: ''
    }
    setDeleteOrderDescription(updateOrderLineDto)
    setIsCancelOrderlineModalOpen(true)
  }

  const handleCancellationOptionChange = (id: string) => {
    if (deleteOrderDescription) {
      const updateDeleteOrderDesc: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelReason: id ? findOrderCancellationLookupCode[id] : ''
      }
      setDeleteOrderDescription(updateDeleteOrderDesc)
    }
  }

  const handleCancellationTextChange = (comment: string) => {
    if (deleteOrderDescription) {
      const updateOrderLineDto: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelComments: comment
      }
      setDeleteOrderDescription(updateOrderLineDto)
    }
  }

  const handleSubmitCancellation = async () => {
    // CLOS-14579: ignore repeat submits while a cancel is in flight or done
    if (isCancellingOrderRef.current) {
      return
    }
    if (order && deleteOrderDescription) {
      isCancellingOrderRef.current = true
      let isCancelled = false
      setCancellationSubmitting(true)
      setIsLoadingCancelOrder(true)

      try {
        // Extract required data from order
        const orderItem = order.lines.find((x) => x.medicationFlag === 'Y')
        const resolvedPatientNumber = orderItem?.patientNumber
        // CLOS-14579: the saga writes the patient (updatePatient, deleteOpa)
        const resolvedPhysicianId = await resolvePatientPhysicianIdOrAnnounce(
          resolvedPatientNumber
        )
        if (!resolvedPhysicianId) {
          setIsCancelOrderlineModalOpen(false)
          return
        }
        const resolvedProgramId = programId?.toString()
        const resolvedPatientId = history.location?.state?.patientId

        // Resolve shipping address ID from multiple sources
        const resolvedShippingAddressId =
          order.deliverTo?.addressId ||
          institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
            ?.addressId ||
          ''

        if (
          !resolvedPhysicianId ||
          !resolvedPatientNumber ||
          !resolvedProgramId
        ) {
          const missingFields = []
          if (!resolvedPhysicianId) missingFields.push('physicianId')
          if (!resolvedPatientNumber) missingFields.push('patientNumber')
          if (!resolvedProgramId) missingFields.push('programId')

          console.error(
            `Missing required data for order cancellation: ${missingFields.join(
              ', '
            )}. Order: ${order.orderNumber}`
          )
          throw new Error(
            'Unable to cancel order due to missing required information.'
          )
        }

        // Log warning if shipping address ID is missing
        if (!resolvedShippingAddressId) {
          console.warn(
            `Warning: No shipping address ID found for order ${order.orderNumber}. ` +
              `This may cause deleteOpa to fail. Checked sources: ` +
              `order.deliverTo.addressId=${order.deliverTo?.addressId}, ` +
              `institute primary address=${
                institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
                  ?.addressId
              }`
          )
        }

        // Create cancellation data using the saga helper
        const cancellationData =
          OrderCancellationSaga.createSimpleOrderCancellation(
            order.orderNumber,
            resolvedPhysicianId,
            resolvedPatientNumber,
            resolvedProgramId,
            resolvedShippingAddressId,
            'InitialOrder', // orderType - may need to be determined from order
            deleteOrderDescription.cancelReason,
            deleteOrderDescription.cancelComments || ''
          )

        // Add patient ID if available
        if (resolvedPatientId) {
          cancellationData.patientId = resolvedPatientId
        }

        // Execute the saga with monitoring utilities
        const transactionId = `ma_order_${order.orderNumber}_${Date.now()}`
        const sagaLogger = createOrderCancellationLogger(transactionId)

        const saga = new OrderCancellationSaga(sagaLogger)
        const result = await saga.cancelOrder(cancellationData)

        // Log the result for monitoring
        logOrderCancellationResult(result)

        if (result.success && result.result) {
          // Check if deleteOpa was successful
          if (!result.result.opaDeleted) {
            console.warn(
              `Order cancellation completed but deleteOpa failed for order ${order.orderNumber}. ` +
                `This may cause cross-system validation issues. Manual cleanup may be required.`
            )
          }

          isCancelled = true
          setIsCancelOrderlineModalOpen(false)
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
            orderId: order.orderNumber,
            programId,
            location: 'Ma Order Detail'
          })
          history.push('/orders')
        } else {
          // Log detailed error information to console
          const errorMessage =
            result.error?.message || 'Order cancellation failed'
          console.error(
            `Order cancellation failed for order ${order.orderNumber}:`,
            {
              error: errorMessage,
              orderNumber: order.orderNumber,
              patientNumber: resolvedPatientNumber,
              programId: resolvedProgramId,
              shippingAddressId: resolvedShippingAddressId,
              result: result
            }
          )

          // Show generic error to user
          throw new Error(
            'Order cancellation failed. Please try again or contact support if the issue persists.'
          )
        }
      } catch (error) {
        // If request is cancelled continue
        if (
          error instanceof Error &&
          error.message === AuthError.RequestCancelled
        ) {
          return
        }

        // Log detailed error information to console
        console.error(
          `Order cancellation error for order ${order.orderNumber}:`,
          {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            orderNumber: order.orderNumber,
            timestamp: new Date().toISOString()
          }
        )

        // Show generic error message to user
        const userErrorMessage =
          error instanceof Error &&
          error.message.includes(
            'Unable to cancel order due to missing required information'
          )
            ? error.message
            : 'There was an error when cancelling your order. Please try again or contact support if the issue persists.'

        setCancellationHasError({
          hasError: true,
          errorMessage: userErrorMessage
        })
        analyticsServiceSingleton.trackError(
          error instanceof Error ? error : new Error(String(error)),
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
      } finally {
        // Keep the guard after success: the page is navigating away
        isCancellingOrderRef.current = isCancelled
        setCancellationSubmitting(false)
        setIsLoadingCancelOrder(false)
      }
    }
  }

  const handleCancellationClose = () => {
    setIsCancelOrderlineModalOpen(false)
    setDeleteOrderDescription(undefined)
  }

  const handleEmailOrderDetails = () => {
    // Validate order status
    if (!isOrderEligibleForInvoice(order)) {
      setOrderAnnounce({
        message: t('ma_order_detail:invoice_download_error'),
        mode: AnnounceMode.Warning,
        isDismissible: true
      })
      return
    }

    if (!order?.orderNumber) {
      return
    }

    setIsDownloadingInvoice(true)

    downloadOrderInvoice(order.orderNumber)
      .then((response) => {
        if (response.status === 200) {
          // Create a URL for the blob
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const downloadUrl = window.URL.createObjectURL(blob)

          // Create a temporary link and click it to download
          const a = document.createElement('a')
          a.href = downloadUrl
          a.download = `invoice_${order.orderNumber}.pdf`
          document.body.appendChild(a)
          a.click()

          // Clean up
          window.URL.revokeObjectURL(downloadUrl)
          document.body.removeChild(a)

          setOrderAnnounce({
            title: ' ',
            message: t('ma_order_detail:invoice_downloaded'),
            mode: AnnounceMode.Success,
            isDismissible: true
          })

          analyticsServiceSingleton.trackEvent(AnalyticsEvent.DownloadInvoice, {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'MA'
          })
        }
      })
      .catch((error: AxiosError) => {
        // Enhanced error handling
        let errorMessage = t('ma_order_detail:invoice_download_error')
        if (error.response?.status === 404) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        } else if (error.response?.status === 403) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        } else if (error.response?.status === 500) {
          errorMessage = t('ma_order_detail:invoice_download_error')
        }

        setOrderAnnounce({
          title: 'Error',
          message: `${errorMessage} ${error}`,
          mode: AnnounceMode.Error,
          isDismissible: true
        })

        analyticsServiceSingleton.trackEvent(
          AnalyticsEvent.DownloadInvoiceError,
          {
            orderNumber: order.orderNumber,
            orderStatus: order.orderStatus,
            deliverToContact: order.deliverToContact,
            shipMethod: order.shipMethod,
            shipToContact: order.shipToContact,
            type: 'MA'
          }
        )
      })
      .finally(() => {
        setIsDownloadingInvoice(false)
      })
  }

  const handleGoBack = () => {
    const target = resolveMaOrderBackTarget(
      history.location.state && history.location.state.from
    )

    if (target.action === 'goBack') {
      history.goBack()
    } else {
      history.push(target.pathname)
    }
  }

  // CLOS-13944: Dismiss only closes the success banner; stay on order confirmation.
  const handleDismissAnnounce = () => {
    setOrderAnnounce(undefined)
  }

  const handleTrackingLinkClicked = (
    orderLine: OrderLineDto,
    currentLanguage: string
  ) => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.DeliveryTracking, {
      carrierName: orderLine.trackingInfo?.freightCarrier,
      instituteId: user?.institute?.instituteId,
      language: currentLanguage,
      location: history.location.pathname
    })
  }

  const handleFileUploadedForOrderAndHold = (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => {
    const holdDocumentType: string | undefined = getHoldDocumentForHold(hold)
    if (hold.holdId && holdDocumentType) {
      setDocumentToHold(
        orderId,
        document.uploadedDocumentId,
        orderLine.lineId,
        hold,
        holdDocumentType
      )
        .then((response) => {
          // Update order with document details
          if (order) {
            setOrder(
              getUpdatedOrderState(
                order,
                orderLine,
                hold,
                holdDocumentType,
                document,
                response?.data?.documentId,
                response?.data?.dataPlatformDocumentId
              )
            )
          }
        })
        .catch((error) => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `An error occurred uploading your document. ${error}`
            )
          )
          // Trigger refresh to remove document
          window.scrollTo(0, 0)
        })
    } else {
      console.warn(
        'No holdId or holdDocumentType supplied to setDocumentToHold'
      )
    }
  }

  const handleFileDeleteForOrderAndHold = (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number,
    dataPlatformDocumentId?: string
  ) => {
    if (
      hold.holdId &&
      (documentId !== undefined || Boolean(dataPlatformDocumentId))
    ) {
      if (order) {
        setOrder(
          getUpdatedOrderStateAfterDelete(
            order,
            orderLine,
            hold,
            documentId,
            dataPlatformDocumentId
          )
        )
      }
    } else {
      console.warn(
        'No holdId or document removal identifier is provided when deleting a document.'
      )
    }
  }

  const listOfNewFeatures = undefined //expired features

  return (
    <>
      <OrderLineCancellationModal
        isOpen={isCancelOrderlineModalOpen}
        selectedOption={
          deleteOrderDescription &&
          findOrderCancellationLookupCode[deleteOrderDescription?.cancelReason]
        }
        cancellationOptions={cancellationOptions}
        isSubmitting={cancellationSubmitting}
        handleCommentChange={handleCancellationTextChange}
        handleOptionChange={handleCancellationOptionChange}
        handleSubmit={handleSubmitCancellation}
        handleClose={handleCancellationClose}
        hasError={cancellationHasError && cancellationHasError.hasError}
        errorMessage={cancellationHasError && cancellationHasError.errorMessage}
      />
      <MaOrderDetail
        isLoading={
          isLoadingPatientAccessForm ||
          isProgramLoading ||
          isPhysiciansLoading ||
          isLoadingCancelOrder
        }
        newFeaturesList={listOfNewFeatures}
        order={order}
        isStockOrder={isStockOrder}
        program={program}
        orderLevelHolds={orderLevelHolds}
        patientAccessFormUrl={patientAccessFormUrl}
        isPhysicianAssociated={isPhysicianAssociatedToUser}
        isEnrolledProgram={isEnrolledProgramToUser}
        orderAnnounce={orderAnnounce}
        isDownloadingInvoice={isDownloadingInvoice}
        trackingInfo={order && getTrackingInfo(order)}
        handleDismissAnnounce={handleDismissAnnounce}
        handlePatientAccessForm={handlePatientAccessForm}
        handleCancelOrder={handleCancelOrder}
        handleOrderResupply={handleOrderResupply}
        isCheckingResupply={isCheckingResupply}
        handleGoBack={handleGoBack}
        handleFileUploadedForOrderAndHold={handleFileUploadedForOrderAndHold}
        handleFileDeleteForOrderAndHold={handleFileDeleteForOrderAndHold}
        handleEmailOrderDetails={handleEmailOrderDetails}
        handleTrackingLinkClicked={handleTrackingLinkClicked}
      />
    </>
  )
}
